import React from 'react';
import {
  HeadingThree,
  Image,
  Flex,
  BodyTwoSecondary,
} from '@leagueplatform/genesis-commons';
import { styled, Icon } from '@leagueplatform/genesis-core';
import {
  isAbsoluteUrl,
  normalizeUrl,
  defaultHandleLink,
} from '@leagueplatform/web-common';
import {
  OpensInANewTabMessage,
  InternalOrExternalLink,
} from '@leagueplatform/web-common-components';
import { useConfigProperty } from '@leagueplatform/core';

const Link = styled(InternalOrExternalLink, {
  '&': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '$oneAndHalf',
    backgroundColor: '$interactiveBackgroundDefault',
    borderWidth: '$thin',
    borderStyle: 'solid',
    borderRadius: '$large',
    borderColor: '$onSurfaceBorderSubdued',
  },
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: '$interactiveBackgroundHovered',
  },
  '&:focus:before': {
    borderRadius: '$large',
  },
});

interface QuickActionMenuItemProps {
  title: string;
  icon?: string;
  url: string;
  description?: string;
  analyticsEvent: () => void;
}

export const QuickActionMenuItem = ({
  title,
  icon,
  url,
  description,
  analyticsEvent,
}: QuickActionMenuItemProps) => {
  const isExternalUrl = isAbsoluteUrl(url);
  const handleLinkCallback = useConfigProperty(
    'core.routing.handleExternalLink',
    defaultHandleLink,
  );

  return (
    <Link
      href={normalizeUrl(url)}
      isExternalUrl={isExternalUrl}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        analyticsEvent();
        if (isExternalUrl) {
          e.preventDefault();
          handleLinkCallback(url);
        }
      }}
    >
      {icon && (
        <Image src={icon} alt="" maxWidth="56px" marginInlineEnd="one" />
      )}
      <Flex width="100%" marginInlineEnd="one" flexDirection="column">
        <HeadingThree width="100%" color="onSurface.text.primary">
          {title}
        </HeadingThree>
        {description && (
          <BodyTwoSecondary width="100%" marginTop="quarter">
            {description}
          </BodyTwoSecondary>
        )}
      </Flex>
      {isExternalUrl && <OpensInANewTabMessage />}
      <Icon icon="interfaceChevronForward" />
    </Link>
  );
};
